import { createContext, useContext, useState, useEffect } from "react";
import { INTERPRETER_BASE_URL } from "../config/runtime";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [permissions, setPermissions] = useState(null);

  const fetchPermissions = async userId => {
    try {
      const res = await fetch(`${INTERPRETER_BASE_URL}/api/user/permissions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        const data = await res.json();
        setPermissions(data);
      }
    } catch (error) {
      console.error("Failed to fetch user permissions:", error);
    }
  };

  useEffect(() => {
    const init = async () => {
      try {
        const storedUser = localStorage.getItem("user");
        const storedToken = localStorage.getItem("token");
        if (storedUser && storedToken) {
          const parsed = JSON.parse(storedUser);
          setUser(parsed);
          setToken(storedToken);
          await fetchPermissions(parsed.id);
        }
      } catch (error) {
        console.error("Failed to load auth data from localStorage:", error);
        localStorage.removeItem("user");
        localStorage.removeItem("token");
      } finally {
        setAuthLoading(false);
      }
    };
    init();
  }, []);

  // Sync user from localStorage when another tab updates it (e.g. after restore).
  useEffect(() => {
    const onStorage = e => {
      if (e.key === "user" && e.newValue) {
        try {
          setUser(JSON.parse(e.newValue));
        } catch (_) {}
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const login = async (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("token", authToken);
    localStorage.setItem("user", JSON.stringify(userData));
    await fetchPermissions(userData.id);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    setPermissions(null);
    localStorage.removeItem("user");
    localStorage.removeItem("token");
  };

  /** Update user (e.g. after restore so session matches DB). Keeps token. */
  const updateUser = userData => {
    setUser(prev => {
      const next = prev ? { ...prev, ...userData } : userData;
      localStorage.setItem("user", JSON.stringify(next));
      return next;
    });
  };

  const signup = async (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", authToken);
    await fetchPermissions(userData.id);
  };

  const authContextValue = {
    user,
    login,
    logout,
    signup,
    updateUser,
    token,
    authLoading,
    permissions,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};
